import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Container,
  Drawer,
  IconButton,
  LinearProgress,
  MenuItem,
  Pagination,
  PaginationItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Bookmark as SavedIcon,
  BookmarkBorder as SaveIcon,
  Close as CloseIcon,
  OpenInFull as OpenPageIcon,
  Search as SearchIcon,
  Tune as FiltersIcon,
} from '@mui/icons-material';
import { jobsApi } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useToast } from '../../hooks/useToast';
import { useSaveJob } from '../../hooks/useSaveJob';
import { Job, JobApplicationState, JobFacets, JobFilters, JobSort } from '../../types';
import { errorMessage } from '../../utils/errors';
import {
  JobBoardState,
  SORT_LABELS,
  activeFilters,
  boardSearch,
  clearedFilters,
  facetApiParams,
  listApiParams,
  normalizeText,
  parseBoardQuery,
  postedAgo,
  totalPages,
  withoutPersonalFilters,
  DEFAULT_BOARD_STATE,
} from '../../utils/jobBoard';
import DebouncedTextField from '../../components/jobs/DebouncedTextField';
import JobCard from '../../components/jobs/JobCard';
import JobDetailView from '../../components/jobs/JobDetailView';
import JobFilterPanel, { BoardChange } from '../../components/jobs/JobFilterPanel';
import PrefillOnOpenControl from '../../components/jobs/PrefillOnOpenControl';
import SavedSearches from '../../components/jobs/SavedSearches';
import { usePrefillOnOpen } from '../../hooks/usePrefillOnOpen';

const SORTS = Object.keys(SORT_LABELS) as JobSort[];
const DETAIL_TITLE_ID = 'job-detail-title';
const SKELETON_CARDS = 8;
/** Wait this long before recounting, so a burst of clicks is one request. */
const FACETS_DELAY_MS = 250;

const gridSx = {
  listStyle: 'none',
  p: 0,
  m: 0,
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: 1.5,
} as const;

/**
 * The job board: search, facet filters, sort and pages, all held in the URL
 * query string so a search can be bookmarked, shared and walked with the back
 * button. A job card opens the company's own posting in a new tab; its
 * details button (and `?job=<id>`) opens the job in a drawer over the list.
 */
const JobBoardPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  const { show, toast } = useToast();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isStaff = useAuthStore((s) => s.isAuthenticated && !!s.user?.is_staff);
  // Jobs carry the viewer's application state, so a change of viewer reloads.
  const viewer = useAuthStore((s) => (s.isAuthenticated ? s.user?.id ?? 'user' : ''));
  // The preference is only offered to signed-in users.
  const [prefillOnOpen] = usePrefillOnOpen();
  const prefill = isAuthenticated ? prefillOnOpen : 'off';

  // ---- URL -> state -------------------------------------------------------

  // The canonical query string of the board part of the URL. Going through it
  // keeps `state` identical while only the open job changes.
  const boardKey = useMemo(() => {
    const parsed = parseBoardQuery(location.search);
    const allowed = isAuthenticated ? parsed : withoutPersonalFilters(parsed);
    // The hiring-system filter is staff's; in anyone else's address it is dropped.
    return boardSearch(isStaff || !allowed.ats.length ? allowed : { ...allowed, ats: [] });
  }, [location.search, isAuthenticated, isStaff]);
  const state = useMemo(() => parseBoardQuery(boardKey), [boardKey]);
  const openJobId = useMemo(() => new URLSearchParams(location.search).get('job'), [location.search]);
  const openedHere = (location.state as { jobDrawer?: boolean } | null)?.jobDrawer === true;

  // Read by the callbacks below so they keep their identity across URL changes.
  const latest = useRef({ state, openJobId, openedHere, pathname: location.pathname });
  latest.current = { state, openJobId, openedHere, pathname: location.pathname };

  // ---- state -> URL -------------------------------------------------------

  /**
   * Change filters, sort or page. Any change other than the page itself goes
   * back to page 1. Typed text replaces the history entry; everything else
   * adds one, so the back button undoes one filter at a time.
   */
  const update: BoardChange = useCallback(
    (patch, options) => {
      const current = latest.current;
      const next: JobBoardState = { ...current.state, page: 1, ...patch };
      const search = boardSearch(next, current.openJobId);
      if (search === boardSearch(current.state, current.openJobId)) return;
      // Two changes in one tick must build on each other, not on the last render.
      latest.current = { ...current, state: next };
      navigate({ pathname: current.pathname, search }, { replace: options?.replace });
    },
    [navigate]
  );

  const clearAll = useCallback(() => update(clearedFilters(latest.current.state)), [update]);
  // A saved search replaces the whole board state (the drawer closes too).
  const openSavedSearch = useCallback(
    (query: string) => {
      const current = latest.current;
      const next = parseBoardQuery(query);
      latest.current = { ...current, state: next, openJobId: null };
      navigate({ pathname: current.pathname, search: boardSearch(next) });
    },
    [navigate]
  );
  const commitSearch = useCallback((value: string) => update({ q: value }, { replace: true }), [update]);

  const openJob = useCallback(
    (job: Job) => {
      const current = latest.current;
      navigate(
        { pathname: current.pathname, search: boardSearch(current.state, job.id) },
        { state: { jobDrawer: true } }
      );
    },
    [navigate]
  );

  const closeJob = useCallback(() => {
    const current = latest.current;
    if (current.openedHere) {
      // Opened from the list: step back to it, so Back does not reopen the job.
      navigate(-1);
    } else {
      // Arrived on a link with the job open: there is no list entry to go back to.
      navigate({ pathname: current.pathname, search: boardSearch(current.state) }, { replace: true });
    }
  }, [navigate]);

  // ---- results ------------------------------------------------------------

  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    // Aborting on every change means a slow response for old filters can
    // never overwrite the results for the current ones.
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    jobsApi
      .getJobs(listApiParams(state), controller.signal)
      .then((data) => {
        const results = Array.isArray(data?.results) ? data.results : [];
        setJobs(results);
        setCount(typeof data?.count === 'number' ? data.count : results.length);
        setLoading(false);
      })
      .catch((e) => {
        if (controller.signal.aborted) return;
        if ((e as { response?: { status?: number } })?.response?.status === 404 && state.page > 1) {
          // A page past the end (the results shrank, or a stale link).
          update({ page: 1 }, { replace: true });
          return;
        }
        setError(errorMessage(e, 'Could not load jobs. Please try again.'));
        setLoading(false);
      });
    return () => controller.abort();
  }, [state, viewer, reloadKey, update]);

  // New results start at the top. Opening or closing a job leaves `state`
  // untouched, so the list keeps its scroll position behind the drawer.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    window.scrollTo({ top: 0 });
  }, [state]);

  // ---- facets -------------------------------------------------------------

  const [facets, setFacets] = useState<JobFacets | null>(null);
  const [facetsFailed, setFacetsFailed] = useState(false);
  const [facetsReloadKey, setFacetsReloadKey] = useState(0);
  // A string, so sort and page changes (which it leaves out) do not recount.
  const facetQuery = useMemo(() => JSON.stringify(facetApiParams(state)), [state]);
  const facetsLoadedOnce = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(
      () => {
        jobsApi
          .getFacets(JSON.parse(facetQuery) as JobFilters, controller.signal)
          .then((data) => {
            facetsLoadedOnce.current = true;
            setFacets(data && typeof data === 'object' ? data : null);
            setFacetsFailed(false);
          })
          .catch(() => {
            if (!controller.signal.aborted) setFacetsFailed(true);
          });
      },
      facetsLoadedOnce.current ? FACETS_DELAY_MS : 0
    );
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [facetQuery, viewer, facetsReloadKey]);

  const retryFacets = useCallback(() => setFacetsReloadKey((key) => key + 1), []);

  // ---- save ---------------------------------------------------------------

  const handleApplicationChange = useCallback((jobId: string, next: JobApplicationState) => {
    setJobs((prev) => (prev ? prev.map((job) => (job.id === jobId ? { ...job, ...next } : job)) : prev));
  }, []);
  const { toggleSave, savingIds } = useSaveJob(show, handleApplicationChange);
  const toggleSaveFromCard = useCallback(
    (job: Job) => {
      const current = latest.current;
      // After signing in, come back to this search with the job open.
      toggleSave(job, { pathname: current.pathname, search: boardSearch(current.state, job.id) });
    },
    [toggleSave]
  );

  // ---- render -------------------------------------------------------------

  const [filtersOpen, setFiltersOpen] = useState(false);
  const chips = useMemo(() => activeFilters(state), [state]);
  const pages = count === null ? 1 : totalPages(count);

  // Keep the job in the drawer while it slides shut.
  const lastJobId = useRef<string | null>(null);
  if (openJobId) lastJobId.current = openJobId;
  const shownJobId = openJobId ?? lastJobId.current;
  const preview = shownJobId ? jobs?.find((job) => job.id === shownJobId) ?? null : null;

  const countText =
    count === null ? '' : `${count.toLocaleString('en-US')} ${count === 1 ? 'job' : 'jobs'}`;
  const updated = postedAgo(facets?.last_updated);
  const boards = facets?.boards;

  const panel = (
    <JobFilterPanel
      state={state}
      facets={facets}
      facetsFailed={facetsFailed}
      onRetryFacets={retryFacets}
      signedIn={isAuthenticated}
      staff={isStaff}
      onChange={update}
    />
  );

  const panelHeader = (onClose?: () => void) => (
    <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.25 }}>
      <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700, flex: 1 }}>
        Filters
      </Typography>
      <Button size="small" onClick={clearAll} disabled={chips.length === 0}>
        Clear all
      </Button>
      {onClose && (
        <IconButton onClick={onClose} aria-label="Close filters" edge="end" sx={{ ml: 0.5 }}>
          <CloseIcon />
        </IconButton>
      )}
    </Stack>
  );

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
          Jobs
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {boards
            ? `Open roles read directly from ${boards.toLocaleString('en-US')} company job boards${updated ? `, updated ${updated}` : ''}.`
            : 'Open roles read directly from company job boards.'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
        {isDesktop && (
          <Paper
            component="aside"
            aria-label="Filters"
            variant="outlined"
            sx={{
              width: 300,
              flexShrink: 0,
              position: 'sticky',
              top: 80, // clears the sticky app bar
              maxHeight: 'calc(100vh - 96px)',
              overflowY: 'auto',
            }}
          >
            {panelHeader()}
            {panel}
          </Paper>
        )}

        <Box component="main" sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
            <DebouncedTextField
              value={state.q}
              onCommit={commitSearch}
              normalize={normalizeText}
              placeholder="Search by title, company or department"
              inputProps={{ 'aria-label': 'Search jobs by title, company or department' }}
              size="small"
              fullWidth
              clearLabel="Clear search"
              startAdornment={<SearchIcon fontSize="small" color="action" />}
              sx={{ bgcolor: 'background.paper' }}
            />
            <Stack direction="row" spacing={1.5}>
              {!isDesktop && (
                <Badge badgeContent={chips.length} color="primary" sx={{ flex: { xs: 1, sm: 'none' } }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<FiltersIcon />}
                    onClick={() => setFiltersOpen(true)}
                    aria-label={chips.length ? `Filters, ${chips.length} active` : 'Filters'}
                    sx={{ height: 40, bgcolor: 'background.paper' }}
                  >
                    Filters
                  </Button>
                </Badge>
              )}
              <TextField
                select
                label="Sort by"
                size="small"
                value={state.sort}
                onChange={(e) => update({ sort: e.target.value as JobSort })}
                sx={{ minWidth: 170, flex: { xs: 1, sm: 'none' }, bgcolor: 'background.paper' }}
              >
                {SORTS.map((sort) => (
                  <MenuItem key={sort} value={sort}>
                    {SORT_LABELS[sort]}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>

          {isAuthenticated && <PrefillOnOpenControl />}
          {isAuthenticated && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              {/* Every job the person saved, whenever it was posted and whoever posted it: the board's usual "past 24 hours" and its hiding of agencies would leave most of them out. */}
              <Button
                size="small"
                variant={state.saved ? 'contained' : 'outlined'}
                startIcon={state.saved ? <SavedIcon /> : <SaveIcon />}
                aria-pressed={state.saved}
                onClick={() => update(state.saved ? { ...DEFAULT_BOARD_STATE } : { ...DEFAULT_BOARD_STATE, saved: true, posted_within: null, hide_agencies: false })}
              >
                Saved jobs
              </Button>
              <Typography variant="caption" color="text.secondary">
                {state.saved ? 'Showing the jobs you saved. Press again for all jobs.' : 'The jobs you bookmarked, whenever they were posted.'}
              </Typography>
            </Stack>
          )}
          {isAuthenticated && <SavedSearches state={state} onOpen={openSavedSearch} notify={show} />}

          {chips.length > 0 && (
            <Stack
              direction="row"
              alignItems="center"
              sx={{ flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}
              role="group"
              aria-label="Active filters"
            >
              {chips.map((chip) => (
                <Chip
                  key={chip.key}
                  size="small"
                  label={chip.label}
                  title={`Remove filter: ${chip.label}`}
                  aria-label={`Remove filter: ${chip.label}`}
                  onClick={() => update(chip.patch)}
                  onDelete={() => update(chip.patch)}
                  sx={{ maxWidth: '100%' }}
                />
              ))}
              <Button size="small" onClick={clearAll}>
                Clear all
              </Button>
            </Stack>
          )}

          <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={2}>
            <Typography variant="subtitle2" component="p" role="status" aria-live="polite" sx={{ fontWeight: 600 }}>
              {error ? '' : count === null ? <Skeleton width={90} /> : countText}
            </Typography>
            {!error && count !== null && pages > 1 && (
              <Typography variant="caption" color="text.secondary">
                Page {state.page.toLocaleString('en-US')} of {pages.toLocaleString('en-US')}
              </Typography>
            )}
          </Stack>
          <Box sx={{ height: 4, my: 0.75 }}>{loading && jobs !== null && <LinearProgress aria-label="Loading jobs" />}</Box>

          {error ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => setReloadKey((key) => key + 1)}>
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          ) : jobs === null ? (
            <Box component="ul" sx={gridSx} aria-busy="true" aria-label="Loading jobs">
              {Array.from({ length: SKELETON_CARDS }).map((_, index) => (
                <Box component="li" key={index}>
                  <Skeleton variant="rounded" height={190} />
                </Box>
              ))}
            </Box>
          ) : jobs.length === 0 ? (
            <Paper variant="outlined" sx={{ textAlign: 'center', py: 6, px: 2 }}>
              <Typography variant="h6" component="h2" gutterBottom>
                {chips.length > 0 ? 'No jobs match' : 'No jobs on the board right now'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: chips.length > 0 ? 2 : 0 }}>
                {chips.length > 0
                  ? 'Try removing a filter, or clear them all to see every job.'
                  : 'New listings are added as company boards are refreshed. Check back soon.'}
              </Typography>
              {chips.length > 0 && (
                <Button variant="contained" onClick={clearAll}>
                  Clear all filters
                </Button>
              )}
            </Paper>
          ) : (
            <Box
              component="ul"
              sx={{ ...gridSx, opacity: loading ? 0.5 : 1, transition: 'opacity 0.15s' }}
              aria-busy={loading}
              aria-label="Jobs"
            >
              {jobs.map((job) => (
                <Box component="li" key={job.id} sx={{ minWidth: 0 }}>
                  <JobCard
                    job={job}
                    selected={job.id === openJobId}
                    saving={savingIds.includes(job.id)}
                    prefill={prefill}
                    onOpen={openJob}
                    onToggleSave={toggleSaveFromCard}
                  />
                </Box>
              ))}
            </Box>
          )}

          {!error && jobs !== null && jobs.length > 0 && pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pages}
                page={Math.min(state.page, pages)}
                siblingCount={isDesktop ? 1 : 0}
                size={isDesktop ? 'medium' : 'small'}
                color="primary"
                // Real links: each page has its own URL and opens in a new tab.
                renderItem={(item) => (
                  <PaginationItem
                    component={RouterLink}
                    to={{ pathname: location.pathname, search: boardSearch({ ...state, page: item.page ?? 1 }) }}
                    {...item}
                  />
                )}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Filters, phone and tablet */}
      <Drawer
        anchor="left"
        open={filtersOpen && !isDesktop}
        onClose={() => setFiltersOpen(false)}
        PaperProps={{
          role: 'dialog',
          'aria-label': 'Filters',
          sx: { width: '100%', maxWidth: 380, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
        }}
      >
        {panelHeader(() => setFiltersOpen(false))}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>{panel}</Box>
        <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button variant="contained" fullWidth size="large" onClick={() => setFiltersOpen(false)}>
            {count === null || loading ? 'Show jobs' : `Show ${countText}`}
          </Button>
        </Box>
      </Drawer>

      {/* Job detail */}
      <Drawer
        anchor="right"
        open={Boolean(openJobId)}
        onClose={closeJob}
        PaperProps={{
          role: 'dialog',
          'aria-modal': true,
          'aria-labelledby': DETAIL_TITLE_ID,
          sx: { width: { xs: '100%', sm: 560, md: 680 }, maxWidth: '100%' },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
            px: { xs: 1, sm: 2 },
            py: 0.75,
          }}
        >
          <Button
            component={RouterLink}
            to={`/jobs/${shownJobId ?? ''}`}
            size="small"
            startIcon={<OpenPageIcon fontSize="small" />}
          >
            Open as page
          </Button>
          <IconButton onClick={closeJob} aria-label="Close job details">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {shownJobId && (
            <JobDetailView
              key={shownJobId}
              jobId={shownJobId}
              preview={preview}
              notify={show}
              onApplicationChange={handleApplicationChange}
              titleId={DETAIL_TITLE_ID}
            />
          )}
        </Box>
      </Drawer>

      {toast}
    </Container>
  );
};

export default JobBoardPage;
